import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Auth } from '@aws-amplify/auth';
import { useHistory } from 'react-router-dom';
import { Box as Wrapper, Space, Stack, Text } from '@tonic-ui/react';
import { Trans, useTranslation } from 'react-i18next';
import { deleteWSSession } from '../../services/ds-api';

import { AuthContext } from '../../context/AuthContext';
import { useStorageState } from '../../hooks/useStorageState';
import validateUserAccount from '../../utils/validate-user-account';
import filterMessage from '../../utils/message-filter';

import useQueryParams from '../../utils/query-params';
import NotificationBanner from '../../components/notification-banner/notification-banner';
import {
  CheckboxInput,
  FormContainer,
  InputField,
  loginValidation,
  SubmitButton,
} from '../../components/forms';

import styles from './login.module.css';
import RouteLink from '../../components/route-link';
import { ExternalLink } from '../../components/external-link';
import { isGovCloudRegion, isMaintenanceModeEnabled } from '../../utils/helpers';
import OverlayLoader from '../../components/loader/overlay-loader';

interface LoginFormProps {
  showAlertBanner?: (arg: Record<string, string | boolean>) => void;
  hideHeading?: boolean;
}

interface LoginFormValues {
  auth: Record<string, any>;
  email: string;
  password: string;
  rememberEmailCheck: boolean;
}

const Login: FC<LoginFormProps> = ({ showAlertBanner, hideHeading }) => {
  const [formErrors, setFormErrors] = useState({
    error: '',
  });

  const { t } = useTranslation();
  const history = useHistory();
  // check to see if we have state that's been pushed to us, for example
  // if the user was just auto-confirmed we can get their email address.
  const {
    location: { state = {} },
  } = history;
  const { email: preConfirmedEmail = '' } = state;
  // see if the user's email address was provided as a query parameter
  const { email: confirmationEmail = '' } = useQueryParams<AuthQueryParams>(window.location);
  const { invitationID = '' } = useQueryParams<AuthQueryParams>(window.location);
  const { setAuthStatus, isAuthenticating, setCognitoUser, eventDistributor } =
    useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(isAuthenticating);
  const focusRef = useRef(null);
  const [uiPref, setUiPref] = useStorageState('localStorage', 'C1C_UI_PREF', {});
  // Show verification banner when login page opened directly on email link click
  const [showConfirmationBanner, setShowConfirmationBanner] = useState({
    confirmationNotice: false,
    message: '',
  });

  const defaultValues: LoginFormValues = useMemo(() => {
    if (uiPref.rememberMe) {
      const email = uiPref.userEmail || '';
      return {
        auth: {},
        email: confirmationEmail ? confirmationEmail : email,
        password: '',
        rememberEmailCheck: true,
      };
    } else {
      return {
        auth: {},
        email: preConfirmedEmail || confirmationEmail,
        password: '',
        rememberEmailCheck: false,
      };
    }
  }, [uiPref.rememberMe]);

  const validateNewRegistration = async () => {
    try {
      setIsLoading(true);
      return await validateUserAccount(location.search);
    } catch (error) {
      setIsLoading(false);
      setFormErrors({
        ...formErrors,
        error: filterMessage(error),
      });
    }
  };

  useEffect(() => {
    if (focusRef.current) {
      focusRef.current = focusRef.current.focus();
    }
  }, []);

  const handleSubmit = async ({
    email,
    password,
    rememberEmailCheck,
    setSubmitting,
    resetForm,
  }) => {
    setFormErrors({
      ...formErrors,
      error: '',
    });

    // Validate the user email
    if (confirmationEmail) {
      await validateNewRegistration();
    }

    // Set rememberMe preference & email
    if (rememberEmailCheck) {
      setUiPref({ ...uiPref, rememberMe: true, userEmail: email });
    } else {
      setUiPref({ ...uiPref, rememberMe: false, userEmail: '' });
    }

    try {
      /**
       * authenticatedUser
       *  return Promise<CognitoUser | any>
       */
      const authenticatedUser = await Auth.signIn(email, password);
      const { challengeName } = authenticatedUser;

      /**
       * Use-case: V1 Logged user switches to v2, their previous session may still be active,
       * Call the delete WS Session ensures any lingering sessions are invalidated
       */
      if (authenticatedUser) {
        await deleteWSSession();
        // Setting this value for the use case when browser cache is cleared for both MFA/Non MFA flows
        localStorage.setItem('C1C_SSO_V2_ENABLED', true);
      }

      if (!isLoading) {
        setIsLoading(true);
      }

      if (challengeName === 'SOFTWARE_TOKEN_MFA' || challengeName === 'SMS_MFA') {
        setCognitoUser(authenticatedUser);
        return eventDistributor.dispatch({
          type: 'NAVIGATE_APP',
          appName: 'mfa-login',
        });
      } else if (challengeName === 'MFA_SETUP') {
        await Auth.setupTOTP(authenticatedUser);
        setAuthStatus(authenticatedUser);
      } else {
        setAuthStatus(authenticatedUser);
      }
    } catch (error) {
      const errMsg = !error.message ? error : error.message;
      console.log('Cognito authentication error: ', errMsg);

      setIsLoading(false);
      setSubmitting(false);
      resetForm();

      if (error.code === 'PasswordResetRequiredException') {
        setFormErrors({
          ...formErrors,
          error: filterMessage(error),
        });
      } else if (error.code === 'UserNotConfirmedException') {
        if (showAlertBanner) {
          showAlertBanner({
            message: error.message,
            confirmationNotice: true,
          });
        } else {
          setShowConfirmationBanner({
            message: error.message,
            confirmationNotice: true,
          });
        }
      } else {
        setFormErrors({
          ...formErrors,
          error: filterMessage(error),
        });

        if (showAlertBanner) {
          showAlertBanner({
            message: '',
            confirmationNotice: false,
          });
        }
      }
    }
  };

  return (
    <>
      {isLoading ? (
        <OverlayLoader isLoading={isLoading} />
      ) : (
        <>
          {isMaintenanceModeEnabled && window.location.pathname === '/login' && (
            <NotificationBanner
              name="maintenance-notice"
              severity="warning"
              showCloseButton={false}
              shouldOpen={true}
              htmlElement={
                <Text as="p" m="0" display="inline-block">
                  <Trans
                    i18nKey={`publicLandingPage.maintenanceNotice.${DEPLOYMENT_TARGET}.message`}
                  />
                  <ExternalLink
                    href={t(`publicLandingPage.maintenanceNotice.${DEPLOYMENT_TARGET}.link`)}
                    textDecoration="none"
                    showIcon={false}
                  >
                    {`${
                      DEPLOYMENT_TARGET === 'stage'
                        ? t(`publicLandingPage.maintenanceNotice.${DEPLOYMENT_TARGET}.link`)
                        : `${window.location.origin}${t(
                            `publicLandingPage.maintenanceNotice.${DEPLOYMENT_TARGET}.link`
                          )}`
                    }`}
                  </ExternalLink>
                  <Space width="1x" />
                  {DEPLOYMENT_TARGET === 'stage' &&
                    t(`publicLandingPage.maintenanceNotice.${DEPLOYMENT_TARGET}.contact`)}
                </Text>
              }
            />
          )}

          {showConfirmationBanner.confirmationNotice && (
            <Wrapper width={850} margin="33px auto">
              <NotificationBanner
                name="confirmation-notice"
                severity="warning"
                showCloseButton={true}
                shouldOpen={showConfirmationBanner.confirmationNotice}
                bannerText="resendConfirmation.bannerTitle"
                bannerButtonTxt="resendConfirmation.bannerBtn"
                clickHandler={() =>
                  eventDistributor.dispatch({
                    type: 'NAVIGATE_APP',
                    appName: 'resend-confirmation',
                  })
                }
              />
            </Wrapper>
          )}
          <Wrapper
            width="360px"
            className={styles.signin_form}
            margin={
              location.pathname.startsWith('/login') && !showConfirmationBanner.confirmationNotice
                ? '128px auto'
                : '0 auto'
            }
          >
            <Text marginBottom="24px" fontWeight={600} fontSize="16px" lineHeight="22px">
              {t('signIn')}
            </Text>
            <Wrapper
              marginBottom={
                formErrors?.error || invitationID !== '' || confirmationEmail !== '' ? '16px' : '0'
              }
            >
              {confirmationEmail !== '' &&
                invitationID === '' &&
                !formErrors?.error &&
                !showConfirmationBanner.confirmationNotice && (
                  <NotificationBanner
                    name="confirmation-email"
                    severity="info"
                    shouldOpen={confirmationEmail !== ''}
                    bannerText={'signUpConfirmation.confirmationMessage'}
                  />
                )}

              {invitationID !== '' && !showConfirmationBanner.confirmationNotice && (
                <NotificationBanner
                  name="invitation-email"
                  severity="info"
                  shouldOpen={invitationID !== ''}
                  bannerText={'signUpConfirmation.invitationConfirmationMessage'}
                />
              )}
              {formErrors?.error && (
                <NotificationBanner
                  name="login-form"
                  severity="error"
                  shouldOpen={true}
                  bannerText={formErrors.error}
                />
              )}
            </Wrapper>
            <FormContainer
              id="login-form"
              customValidation={loginValidation}
              initialValues={defaultValues}
              enableReinitialize={true}
              submitAction={(formPayload) => handleSubmit(formPayload)}
            >
              <Stack direction="column" spacing="4x">
                {!hideHeading && (
                  <Text size="2xl" lineHeight="xl" fontWeight={300} data-id="login-form-heading">
                    {t('signIn')}
                  </Text>
                )}
                <Wrapper>
                  <InputField
                    id="email"
                    type="email"
                    label={t('email')}
                    name="email"
                    isRequired={true}
                    ref={focusRef}
                  />
                </Wrapper>
                <Wrapper>
                  <InputField
                    id="password"
                    type="password"
                    label={t('password')}
                    name="password"
                    isRequired={true}
                  />
                  <RouteLink href="/forgot-password" marginTop={5} id="login-forgot-password-link">
                    {t('forgotPassword')}
                  </RouteLink>
                </Wrapper>
                {!isGovCloudRegion && (
                  <RouteLink href="/legacy-login" id="login-legacy-rescue-link">
                    {t('legacyRescueLinkText')}
                  </RouteLink>
                )}
                <CheckboxInput label={t('rememberMe')} name="rememberEmailCheck" />
                <SubmitButton label={t('signIn')} id="login-form-submit-btn" />
              </Stack>
            </FormContainer>
          </Wrapper>
        </>
      )}
    </>
  );
};

export default Login;
